import React, {
  BaseSyntheticEvent,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import {
  Status,
  StatusIndicator,
} from "@amzn/stencil-react-components/status-indicator";
import { useVideoData } from "../../swr-hooks/new-hire-video";
import * as adobeAnalytics from "../../utility/adobe-analytics";
import { NewHireVideoProps } from "./new-hire-video-props";
import { format } from "date-fns";
import { isDragonstone } from "../../helpers/url-params";
import { useParams } from "react-router";
import useArb from "../../hooks/use-arb";

export enum ReferrerMode {
  Ash = "ash",
  NonAsh = "non-ash",
}

export const NewHireVideo = ({
  applicationId,
  videoRecordQueryUrl,
  watchedTime,
  updateWatchedTime,
  videoPlayedAt,
  updateVideoPlayedAt,
  updateCurrentTime,
  updateLastPlay,
  lastPlay,
  isEnglish,
}: NewHireVideoProps) => {
  const { data: videoData, error } = useVideoData(applicationId);
  const videoRef = useRef<HTMLVideoElement>(null);
  const bundle = useArb("HVHChecklistPantherResource");
  useEffect(() => {
    const handleVisibilitychange = async () => {
      if (document.visibilityState !== "visible") {
        const video = videoRef.current;
        if (video) {
          video.pause();
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilitychange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilitychange);
    };
  });

  const onVideoPlay = () => {
    if (videoPlayedAt === "") {
      updateVideoPlayedAt(format(new Date(), "yyyy-MM-dd HH:mm:ss"));
    }
  };
  const { requisitionId } = useParams<{
    requisitionId: string;
  }>();
  const onVideoEnd = useCallback(() => {
    adobeAnalytics.addEventMetric(
      window,
      applicationId,
      "finish new hire video",
      {
        referrer: adobeAnalytics.interpretReferrer(document.referrer),
        jobId: requisitionId,
        isDragonstone: isDragonstone(requisitionId),
      }
    );
  }, [applicationId, requisitionId]);

  const loadErrorMessage = bundle.getMessage("Checklist-NHE-UnableLoad");

  const onVideoSeeking = (e: BaseSyntheticEvent) => {
    updateLastPlay(e.target.currentTime);
    updateCurrentTime(e.target.currentTime);
  };

  const onVideoTimeUpdate = (e: BaseSyntheticEvent) => {
    if (!e.target.seeking) {
      if (e.target.currentTime > watchedTime) {
        updateWatchedTime(e.target.currentTime - lastPlay);
      }
      updateCurrentTime(e.target.currentTime);
    }
  };

  const onLoadedmetadata = (e: BaseSyntheticEvent) => {
    const lastCur = !localStorage.getItem("videoCurrentTime")
      ? 0
      : parseFloat(localStorage.getItem("videoCurrentTime") as string);
    e.target.currentTime = lastCur;
    updateCurrentTime(lastCur);
    updateLastPlay(lastCur);
    updateWatchedTime(0);
  };

  if (error) {
    return (
      <StatusIndicator
        status={Status.Negative}
        messageText={loadErrorMessage}
      />
    );
  }

  if (!videoData) {
    return <Spinner showText size={SpinnerSize.Large} />;
  }

  if (isEnglish) {
    return (
      <>
        <video
          ref={videoRef}
          id="newHireVideo"
          width="100%"
          controls
          onPlay={onVideoPlay}
          onEnded={onVideoEnd}
          onSeeking={onVideoSeeking}
          onTimeUpdate={onVideoTimeUpdate}
          onLoadedMetadata={onLoadedmetadata}
          data-test-record-url={videoRecordQueryUrl}
          crossOrigin="anonymous"
        >
          <source src={(videoData as any).uri} type="video/mp4" />
        </video>
      </>
    );
  }
  return (
    <>
      <video
        ref={videoRef}
        id="newHireVideo"
        width="100%"
        controls
        onPlay={onVideoPlay}
        onEnded={onVideoEnd}
        onSeeking={onVideoSeeking}
        onTimeUpdate={onVideoTimeUpdate}
        onLoadedMetadata={onLoadedmetadata}
        data-test-record-url={videoRecordQueryUrl}
        crossOrigin="anonymous"
        controlsList="nodownload noplaybackrate"
        disablePictureInPicture
      >
        <source src={(videoData as any).uri} type="video/mp4" />
      </video>
    </>
  );
};
